import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { NoteDocument } from "@verity-connect/domain";
import type {
  AttendeeNoteConflictResolutionRequest,
  AttendeeNoteList,
  AttendeeNoteMarkerKind,
  AttendeeNoteSyncResult,
  AttendeeNoteValues,
  AttendeeSession,
} from "@verity-connect/contracts";
import { readClientId } from "./personal";
import {
  acceptRemoteNote,
  adoptGuestPrivateNotes,
  blankPrivateNote,
  editPrivateNote,
  listPrivateNotes,
  putPrivateNote,
  type LocalPrivateNote,
} from "./private-notes";
import { attendeeRunIsCurrent, emitAttendeeStateChanged, useAttendeeStateEpoch } from "./cross-device-state";

export type PrivateNoteStatus = "empty" | "device" | "syncing" | "synced" | "offline" | "conflict" | "unavailable";

export function usePrivateNotes(session: AttendeeSession | null, eventId: string, eventSlug: string, validItemIds: readonly string[], online: boolean) {
  const signal = useAttendeeStateEpoch();
  const notesPath = `/api/attendee/events/${encodeURIComponent(eventSlug)}/notes`;
  const identityScope = session?.identityId ?? "guest";
  const [notes, setNotes] = useState<Record<string, LocalPrivateNote>>({});
  const [ready, setReady] = useState(false);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const syncing = useRef(new Set<string>());
  const syncGeneration = useRef(0);
  const scope = `${identityScope}:${eventId}`;
  const scopeRef = useRef(scope);
  if (scopeRef.current !== scope) {
    syncGeneration.current += 1;
    syncing.current.clear();
  }
  scopeRef.current = scope;
  const validItemsKey = validItemIds.join("|");
  const validSet = useMemo(() => new Set(validItemIds), [validItemsKey]);

  const persist = useCallback((note: LocalPrivateNote) => {
    void putPrivateNote(note).catch((reason: unknown) => setStorageError(reason instanceof Error ? reason.message : "This note could not be saved on this device."));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setStorageError(null);
    setSyncError(null);
    void (async () => {
      const loadScope = `${identityScope}:${eventId}`;
      const current = () => !cancelled && scopeRef.current === loadScope;
      try {
        await adoptGuestPrivateNotes(identityScope, eventId);
        if (!current()) return;
        const stored = (await listPrivateNotes(identityScope, eventId)).filter((note) => validSet.has(note.itemId));
        if (!current()) return;
        const localByItem = new Map(stored.map((note) => [note.itemId, note]));
        if (session && online) {
          try {
            const response = await fetch(notesPath, { credentials: "same-origin" });
            if (!response.ok) throw new Error("Your synchronized notes could not be loaded. Device copies remain available.");
            const remote = await response.json() as AttendeeNoteList;
            if (!current()) return;
            for (const note of remote.notes) {
              if (!current()) return;
              if (!validSet.has(note.itemId)) continue;
              const merged = acceptRemoteNote(localByItem.get(note.itemId), note, identityScope, eventId);
              localByItem.set(note.itemId, merged);
              await putPrivateNote(merged);
            }
            for (const conflict of remote.conflicts) {
              if (!current()) return;
              if (!validSet.has(conflict.itemId)) continue;
              const existing = localByItem.get(conflict.itemId);
              const base = existing ?? {
                ...acceptRemoteNote(undefined, conflict.current, identityScope, eventId),
                values: conflict.incomingValues,
                savedAt: conflict.incomingClientUpdatedAt,
              };
              const conflicted = { ...base, serverRevision: conflict.current.revision, pending: null, conflict };
              localByItem.set(conflict.itemId, conflicted);
              await putPrivateNote(conflicted);
            }
          } catch (reason) {
            if (!cancelled) setSyncError(reason instanceof Error ? reason.message : "Synchronized notes could not be loaded. Device copies remain available.");
          }
        }
        if (!cancelled) setNotes(Object.fromEntries(localByItem));
      } catch (reason) {
        if (!cancelled) {
          setStorageError(reason instanceof Error ? reason.message : "Private notes could not be loaded.");
          try {
            const stored = await listPrivateNotes(identityScope, eventId);
            if (!cancelled) setNotes(Object.fromEntries(stored.filter((note) => validSet.has(note.itemId)).map((note) => [note.itemId, note])));
          } catch { /* The first error already explains the unavailable storage. */ }
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, identityScope, notesPath, online, session?.identityId, validItemsKey, signal.epoch]);

  useEffect(() => {
    if (signal.reason !== "signed-out" && signal.reason !== "account-deleted") return;
    syncGeneration.current += 1;
    syncing.current.clear();
    setNotes({});
    setReady(false);
  }, [signal.epoch, signal.reason]);

  const updateValues = useCallback((itemId: string, update: (values: AttendeeNoteValues) => AttendeeNoteValues) => {
    if (!validSet.has(itemId)) return;
    setNotes((current) => {
      const previous = current[itemId] ?? blankPrivateNote(identityScope, eventId, itemId);
      const next = editPrivateNote(previous, update(previous.values), new Date().toISOString());
      persist(next);
      return { ...current, [itemId]: next };
    });
  }, [eventId, identityScope, persist, validItemsKey]);

  const updateBody = useCallback((itemId: string, body: string) => updateValues(itemId, (values) => ({ ...values, body })), [updateValues]);
  /**
   * The document and the text it flattens to, written together and never apart.
   *
   * Three things read the text — the conflict screen, her credit evidence, and search —
   * and none of them may disagree with what she sees on screen. Writing both in one
   * update is what makes that impossible rather than merely intended.
   */
  const updateDocument = useCallback(
    (itemId: string, next: { document: NoteDocument; body: string }) =>
      updateValues(itemId, (values) => ({ ...values, document: next.document, body: next.body })),
    [updateValues],
  );
  const addMarker = useCallback((itemId: string, kind: AttendeeNoteMarkerKind) => updateValues(itemId, (values) => {
    const now = new Date().toISOString();
    return { ...values, markers: [...values.markers, { id: crypto.randomUUID(), kind, text: "", createdAt: now, updatedAt: now }] };
  }), [updateValues]);
  const updateMarker = useCallback((itemId: string, markerId: string, text: string) => updateValues(itemId, (values) => ({
    ...values,
    markers: values.markers.map((marker) => marker.id === markerId ? { ...marker, text, updatedAt: new Date().toISOString() } : marker),
  })), [updateValues]);
  const removeMarker = useCallback((itemId: string, markerId: string) => updateValues(itemId, (values) => ({ ...values, markers: values.markers.filter((marker) => marker.id !== markerId) })), [updateValues]);

  const pendingToken = Object.values(notes).map((note) => note.pending?.id ?? "").join("|");
  useEffect(() => {
    if (!session || !online || !ready) return;
    const next = Object.values(notes).find((note) => note.pending && !note.conflict && !syncing.current.has(note.itemId));
    if (!next?.pending) return;
    const sent = next.pending;
    const generation = syncGeneration.current;
    const runScope = `${session.identityId}:${eventId}`;
    const captured = { scope: runScope, generation };
    const current = () => attendeeRunIsCurrent(captured, { scope: scopeRef.current, generation: syncGeneration.current });
    syncing.current.add(next.itemId);
    setNotes((current) => {
      const currentNote = current[next.itemId];
      return currentNote ? { ...current, [next.itemId]: { ...currentNote } } : current;
    });
    void fetch(`${notesPath}/sync`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ clientId: readClientId(), mutation: { id: sent.id, itemId: next.itemId, baseRevision: sent.baseRevision, values: next.values, clientUpdatedAt: sent.clientUpdatedAt } }),
    }).then(async (response) => {
      if (!response.ok) throw new Error("The note could not be synchronized yet.");
      if (!current()) return;
      setSyncError(null);
      const result = await response.json() as AttendeeNoteSyncResult;
      if (!current()) return;
      emitAttendeeStateChanged("write");
      setNotes((current) => {
        const latest = current[next.itemId];
        if (!latest) return current;
        let updated: LocalPrivateNote;
        if (result.conflict) {
          updated = { ...latest, serverRevision: result.note.revision, pending: null, conflict: result.conflict, synchronizedAt: result.synchronizedAt };
        } else if (latest.pending?.id === sent.id) {
          updated = { ...latest, values: result.note.values, serverRevision: result.note.revision, pending: null, conflict: null, savedAt: result.note.clientUpdatedAt, synchronizedAt: result.synchronizedAt };
        } else {
          updated = { ...latest, serverRevision: result.note.revision, pending: latest.pending ? { ...latest.pending, baseRevision: result.note.revision } : null, synchronizedAt: result.synchronizedAt };
        }
        persist(updated);
        return { ...current, [next.itemId]: updated };
      });
    }).catch((reason: unknown) => {
      if (current()) setSyncError(reason instanceof Error ? reason.message : "The note could not be synchronized yet.");
    })
      .finally(() => {
        syncing.current.delete(next.itemId);
        if (current()) setNotes((latest) => ({ ...latest }));
      });
    // `signal.epoch` is here so that being asked to check in retries a note whose send
    // failed. Without it a note that failed once waited for the network to go away and come
    // back, or for the application to be reloaded — and a phone that is woken by a silent
    // push to send exactly this note would have woken for nothing.
  }, [eventId, notesPath, online, pendingToken, ready, session?.identityId, signal.epoch]);

  const resolveConflict = useCallback(async (itemId: string, action: "keep_current" | "use_incoming") => {
    const note = notes[itemId];
    if (!session || !note?.conflict) return;
    const captured = { scope: `${session.identityId}:${eventId}`, generation: syncGeneration.current };
    const isCurrent = () => attendeeRunIsCurrent(captured, { scope: scopeRef.current, generation: syncGeneration.current });
    const request: AttendeeNoteConflictResolutionRequest = action === "use_incoming" ? { action: "merge", mergedValues: note.values } : { action };
    const response = await fetch(`${notesPath}/conflicts/${encodeURIComponent(note.conflict.id)}/resolve`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error("The note versions could not be resolved.");
    const result = await response.json() as AttendeeNoteSyncResult;
    if (!isCurrent()) return;
    const updated: LocalPrivateNote = {
      ...note,
      values: result.note.values,
      serverRevision: result.note.revision,
      pending: null,
      conflict: result.conflict,
      savedAt: result.note.clientUpdatedAt,
      synchronizedAt: result.synchronizedAt,
    };
    if (!isCurrent()) return;
    persist(updated);
    setNotes((current) => isCurrent() ? { ...current, [itemId]: updated } : current);
  }, [eventId, notes, notesPath, persist, session?.identityId]);

  const statusFor = useCallback((itemId: string): PrivateNoteStatus => {
    if (storageError) return "unavailable";
    const note = notes[itemId];
    if (!note || (!note.values.body.trim() && note.values.markers.length === 0)) return "empty";
    if (note.conflict) return "conflict";
    if (syncing.current.has(itemId)) return "syncing";
    if (note.pending) return online && session ? "device" : online ? "device" : "offline";
    return session ? "synced" : "device";
  }, [notes, online, session?.identityId, storageError]);

  return { notes, ready, storageError, syncError, updateBody, updateDocument, addMarker, updateMarker, removeMarker, resolveConflict, statusFor };
}
